/**
 * Direct Gemini Live API Client
 *
 * Connects directly to Gemini Live API using ephemeral tokens.
 * Features:
 * - Affective dialog (emotion-aware responses)
 * - Session resumption (pause/resume within 2hr)
 * - Automatic VAD (always-listening mode)
 * - Context window compression
 * - Token usage tracking
 */

import { GoogleGenAI, Modality } from '@google/genai';
import { getTokenService } from './tokenService';
import type { GeminiClientConfig, UsageMetadata, GeminiClientCallbacks, GeminiFunctionCall, GeminiFunctionResponse } from '../types/gemini';

// Session handle storage keys
const SESSION_HANDLE_KEY = 'gemini_direct_session_handle';
const SESSION_HANDLE_TIMESTAMP_KEY = 'gemini_direct_session_timestamp';
const SESSION_RESUME_WINDOW = 2 * 60 * 60 * 1000; // 2 hours

export class GeminiDirectClient {
  private client: GoogleGenAI | null = null;
  private session: any = null; // LiveSession type from SDK
  private config: GeminiClientConfig;
  private callbacks: Partial<GeminiClientCallbacks>;
  private tokenService = getTokenService();

  private isConnected = false;
  private shouldReconnect = true;
  private isPaused = false;
  private sessionHandle: string | null = null;

  // Token usage tracking
  private tokenUsage: UsageMetadata = {
    inputTokens: 0,
    outputTokens: 0,
    totalTokens: 0
  };

  // Auto-nudge detection
  private lastAudioSentTime: number = 0;
  private lastResponseTime: number = 0;
  private autoNudgeTimer: NodeJS.Timeout | null = null;
  private hasNudged: boolean = false;
  private static readonly AUTO_NUDGE_DELAY_MS = 3000; // Wait 3s before auto-nudge

  constructor(config: GeminiClientConfig) {
    this.config = config;
    this.callbacks = config.callbacks;
    // Only use sessionHandle if explicitly passed (e.g., for resume)
    // Don't auto-load from localStorage - that's handled by resume() explicitly
    this.sessionHandle = config.sessionHandle || null;
  }

  /**
   * Connect to Gemini Live API
   */
  async connect(): Promise<void> {
    if (this.isConnected) {
      console.log('[GeminiClient] Already connected');
      return;
    }

    // If this is a fresh connection (not resuming), clear any stale session handles
    // This prevents using an old handle from a previous session
    if (!this.sessionHandle) {
      const storedHandle = this.getStoredSessionHandle();
      if (storedHandle) {
        console.log('[GeminiClient] Clearing stale session handle from previous session');
        this.clearStoredSessionHandle();
      }
    }

    try {
      // Get ephemeral token from backend (includes voice, tasks, and lesson type in locked config)
      const ephemeralToken = await this.tokenService.getToken(
        this.config.userId,
        this.config.systemPrompt,
        false, // forceRefresh
        this.config.voiceName,
        this.config.tasks,
        this.config.isReviewLesson
      );

      // Initialize client with ephemeral token as API key
      // Using v1alpha for ephemeral token and affective dialog support
      this.client = new GoogleGenAI({
        apiKey: ephemeralToken.token,
        httpOptions: { apiVersion: 'v1alpha' }
      });

      // Build live config
      const liveConfig = this.buildLiveConfig();

      console.log('[GeminiClient] Connecting to Gemini Live API...');
      console.log('[GeminiClient] Session handle for resume:', this.sessionHandle ? this.sessionHandle.substring(0, 30) + '...' : 'none');
      console.log('[GeminiClient] Config sessionResumption:', JSON.stringify(liveConfig.sessionResumption));

      // Connect to live session
      this.session = await this.client.live.connect({
        model: ephemeralToken.model,
        config: liveConfig,
        callbacks: {
          onopen: () => {
            console.log('[GeminiClient] WebSocket opened');
          },
          onmessage: (message: any) => {
            this.handleMessage(message);
          },
          onerror: (error: any) => {
            console.error('[GeminiClient] WebSocket error:', error);
            this.callbacks.onError?.(new Error(error?.message || 'WebSocket error'));
          },
          onclose: (event: any) => {
            console.log('[GeminiClient] WebSocket closed:', event?.reason);
            this.handleDisconnect();
          }
        }
      });

      this.isConnected = true;
      this.isPaused = false;
      console.log('[GeminiClient] Connected successfully');
      this.callbacks.onConnected?.();

    } catch (error) {
      console.error('[GeminiClient] Connection failed:', error);
      this.callbacks.onError?.(error as Error);
      throw error;
    }
  }

  /**
   * Build the Live API configuration
   */
  private buildLiveConfig(): any {
    const config: any = {
      responseModalities: [Modality.AUDIO],
      // Enable audio transcription for chat bubbles
      outputAudioTranscription: {},  // Transcribe AI's spoken responses
      inputAudioTranscription: {},   // Transcribe user's spoken input
      speechConfig: {
        voiceConfig: {
          prebuiltVoiceConfig: {
            voiceName: this.config.voiceName || 'Aoede'
          }
        }
      },
      realtimeInputConfig: {
        automaticActivityDetection: {
          disabled: false,
          startOfSpeechSensitivity: 'START_SENSITIVITY_HIGH', // More sensitive speech detection
          endOfSpeechSensitivity: 'END_SENSITIVITY_LOW',      // Wait longer before ending (fewer cutoffs)
          prefixPaddingMs: 200,                               // Standard lead-in
          silenceDurationMs: 1000                             // 1 second silence before ending
        }
      },
      // Session resumption - pass handle if we have one
      sessionResumption: this.sessionHandle ? { handle: this.sessionHandle } : {},
      // Context window compression for longer sessions
      contextWindowCompression: {
        slidingWindow: {}
      },
      // Enable affective dialog for emotion-aware responses
      enableAffectiveDialog: true
    };

    // NOTE: System instruction AND function calling tools are already baked
    // into the ephemeral token by the Python backend (token_service.py).
    // We don't pass them here to avoid conflicts with locked token config.

    return config;
  }

  /**
   * Handle incoming messages from Gemini
   */
  private handleMessage(message: any): void {
    // Debug: log all messages to see what we're receiving
    console.log('[GeminiClient] Message received:', Object.keys(message));

    // Deep debug: log full message structure for tool call debugging
    if (message.toolCall || message.serverContent?.modelTurn?.parts?.some((p: any) => p.functionCall || p.executableCode)) {
      console.log('[GeminiClient] 🔧 TOOL CALL DETECTED - Full message:', JSON.stringify(message, null, 2));
    }

    // Additional debug: log modelTurn parts to see what types are being sent
    if (message.serverContent?.modelTurn?.parts) {
      const partTypes = message.serverContent.modelTurn.parts.map((p: any) => {
        const keys = Object.keys(p);
        // Check for any function-related keys
        if (keys.some(k => k.toLowerCase().includes('function') || k.toLowerCase().includes('tool'))) {
          console.log('[GeminiClient] 🔧 Potential function call in part:', JSON.stringify(p));
        }
        return keys.join(',');
      });
      console.log('[GeminiClient] Part types:', partTypes);
    }

    // Session resumption update - store new handle
    // Check for both resumable flag and newHandle
    if (message.sessionResumptionUpdate) {
      console.log('[GeminiClient] Session resumption update:', message.sessionResumptionUpdate);
      if (message.sessionResumptionUpdate.resumable && message.sessionResumptionUpdate.newHandle) {
        this.sessionHandle = message.sessionResumptionUpdate.newHandle;
        this.storeSessionHandle(this.sessionHandle!);
        this.callbacks.onSessionUpdate?.(this.sessionHandle!);
        console.log('[GeminiClient] ✅ Session resumed successfully, new handle:', this.sessionHandle?.substring(0, 20) + '...');
      } else if (Object.keys(message.sessionResumptionUpdate).length === 0 && this.sessionHandle) {
        // Empty update when we had a handle means resumption failed - session context was lost
        console.warn('[GeminiClient] ⚠️ Session resumption FAILED - conversation context lost (Gemini server-side issue)');
        this.sessionHandle = null;
        // Also clear localStorage so we don't keep trying to resume with stale handle
        this.clearStoredSessionHandle();
        this.callbacks.onResumptionFailed?.();
      }
    }

    // GoAway notification - connection will close soon
    if (message.goAway) {
      const timeLeft = message.goAway.timeLeft || 60;
      console.log(`[GeminiClient] GoAway received, ${timeLeft}s remaining`);
      this.callbacks.onGoAway?.(timeLeft);
    }

    // Server content (audio/text responses)
    if (message.serverContent) {
      const content = message.serverContent;

      // Response received - cancel auto-nudge and track time
      this.lastResponseTime = Date.now();
      this.cancelAutoNudge();

      // Handle model turn parts
      if (content.modelTurn?.parts) {
        console.log('[GeminiClient] Model turn parts:', content.modelTurn.parts.map((p: any) => Object.keys(p)));

        for (const part of content.modelTurn.parts) {
          // Audio response
          if (part.inlineData) {
            const audioData = this.base64ToArrayBuffer(part.inlineData.data);
            this.callbacks.onAudio?.(audioData);
          }
          // Text response
          if (part.text) {
            this.callbacks.onText?.(part.text);
          }
          // Thought/reasoning (new in 2.5 models) - log but don't display
          if (part.thought) {
            const thoughtStr = typeof part.thought === 'string'
              ? part.thought
              : JSON.stringify(part.thought);
            console.log('[GeminiClient] 🧠 Model thought:', thoughtStr.substring(0, 200));
          }
          // Function call in parts (new format in some 2.5 models)
          if (part.functionCall) {
            console.log('[GeminiClient] 🔧 Function call in modelTurn.parts:', part.functionCall);
            // Handle this as a tool call
            this.handleToolCall({ functionCalls: [part.functionCall] });
          }
        }
      }

      // User interrupted AI's response (started speaking while AI was talking)
      // This flag is set when VAD detects user speech during model output
      if (content.interrupted) {
        console.log('[GeminiClient] 🛑 User interrupted AI response');
        this.callbacks.onInterrupted?.();
      }

      // Turn complete
      if (content.turnComplete) {
        this.callbacks.onTurnComplete?.();
      }

      // Output transcription (AI's spoken response transcribed to text)
      if (content.outputTranscription?.text) {
        console.log('[GeminiClient] Output transcription:', content.outputTranscription.text);
        this.callbacks.onOutputTranscription?.(content.outputTranscription.text);
      }

      // Input transcription (user's spoken input transcribed to text)
      if (content.inputTranscription?.text) {
        console.log('[GeminiClient] Input transcription:', content.inputTranscription.text);
        this.callbacks.onInputTranscription?.(content.inputTranscription.text);
      }
    }

    // Audio data directly (some responses)
    if (message.data && !message.serverContent) {
      const audioData = this.base64ToArrayBuffer(message.data);
      this.callbacks.onAudio?.(audioData);
    }

    // Usage metadata for token tracking
    if (message.usageMetadata) {
      const metadata: UsageMetadata = {
        inputTokens: message.usageMetadata.inputTokens || 0,
        outputTokens: message.usageMetadata.outputTokens || 0,
        totalTokens: message.usageMetadata.totalTokens || 0
      };

      // Accumulate usage
      this.tokenUsage.inputTokens += metadata.inputTokens;
      this.tokenUsage.outputTokens += metadata.outputTokens;
      this.tokenUsage.totalTokens += metadata.totalTokens;

      this.callbacks.onUsageMetadata?.(metadata);
    }

    // Tool calls (function calling) - Gemini wants us to execute a function
    if (message.toolCall) {
      console.log('[GeminiClient] 🔧 Tool call received (top-level):', JSON.stringify(message.toolCall, null, 2));
      this.handleToolCall(message.toolCall);
    }

    // Check for tool calls in other locations (Gemini 2.5 model variations)
    if (message.toolCallCancellation) {
      console.log('[GeminiClient] ⚠️ Tool call cancellation:', message.toolCallCancellation);
    }
  }

  /**
   * Handle tool/function calls from Gemini
   */
  private async handleToolCall(toolCall: any): Promise<void> {
    console.log('[GeminiClient] 🔧 handleToolCall called with:', JSON.stringify(toolCall, null, 2));

    if (!toolCall.functionCalls || !Array.isArray(toolCall.functionCalls)) {
      console.warn('[GeminiClient] ⚠️ Invalid tool call format - no functionCalls array:', toolCall);
      // Check if it's a single function call (not array)
      if (toolCall.name && (toolCall.args || toolCall.id)) {
        console.log('[GeminiClient] 📝 Single function call format detected, converting to array');
        toolCall = { functionCalls: [toolCall] };
      } else {
        return;
      }
    }

    // Convert to our typed format
    const functionCalls: GeminiFunctionCall[] = toolCall.functionCalls.map((fc: any) => ({
      id: fc.id || `auto-${Date.now()}`, // Generate ID if missing
      name: fc.name,
      args: fc.args || {},
    }));

    console.log('[GeminiClient] 🔧 Processing', functionCalls.length, 'function call(s)');
    functionCalls.forEach((fc, i) => {
      console.log(`[GeminiClient] 🔧 Function ${i + 1}: ${fc.name}`, JSON.stringify(fc.args));
    });

    // Check if we have a handler
    if (!this.callbacks.onToolCall) {
      console.warn('[GeminiClient] No onToolCall handler registered, sending empty responses');
      // Send empty responses so Gemini doesn't hang
      const emptyResponses: GeminiFunctionResponse[] = functionCalls.map(fc => ({
        id: fc.id,
        name: fc.name,
        response: { result: 'No handler registered' }
      }));
      await this.sendToolResponse(emptyResponses);
      return;
    }

    try {
      // Call the handler and get responses
      const responses = await this.callbacks.onToolCall(functionCalls);

      // Send responses back to Gemini
      await this.sendToolResponse(responses);
    } catch (error) {
      console.error('[GeminiClient] Error in onToolCall handler:', error);

      // Send error responses
      const errorResponses: GeminiFunctionResponse[] = functionCalls.map(fc => ({
        id: fc.id,
        name: fc.name,
        response: { result: 'Error', error: String(error) }
      }));
      await this.sendToolResponse(errorResponses);
    }
  }

  /**
   * Send tool/function responses back to Gemini
   */
  async sendToolResponse(responses: GeminiFunctionResponse[]): Promise<void> {
    if (!this.session || !this.isConnected) {
      console.warn('[GeminiClient] Cannot send tool response - not connected');
      return;
    }

    try {
      console.log('[GeminiClient] Sending tool responses:', responses.map(r => r.name).join(', '));

      await this.session.sendToolResponse({
        functionResponses: responses
      });

      console.log('[GeminiClient] Tool responses sent successfully');
    } catch (error) {
      console.error('[GeminiClient] Error sending tool response:', error);
    }
  }

  /**
   * Handle disconnect
   */
  private handleDisconnect(): void {
    const wasConnected = this.isConnected;
    this.isConnected = false;
    this.session = null;

    if (wasConnected) {
      this.callbacks.onDisconnected?.();
    }

    // Auto-reconnect if not paused and should reconnect
    if (this.shouldReconnect && !this.isPaused) {
      console.log('[GeminiClient] Auto-reconnecting in 2s...');
      setTimeout(() => {
        if (this.shouldReconnect && !this.isPaused) {
          this.tokenService.clearCache(); // Get fresh token
          this.connect().catch(console.error);
        }
      }, 2000);
    }
  }

  /**
   * Schedule auto-nudge if no response received
   */
  private scheduleAutoNudge(): void {
    // Clear existing timer
    if (this.autoNudgeTimer) {
      clearTimeout(this.autoNudgeTimer);
    }

    this.autoNudgeTimer = setTimeout(async () => {
      // Only nudge if we sent audio more recently than we received a response
      if (this.lastAudioSentTime > this.lastResponseTime && !this.hasNudged) {
        console.log('[GeminiClient] Auto-nudging - no response after audio');
        this.hasNudged = true;

        // Try audioStreamEnd first (flushes cached audio)
        await this.nudgeResponse();

        // If still no response after 2s, force with turnComplete
        setTimeout(async () => {
          if (this.lastAudioSentTime > this.lastResponseTime) {
            console.log('[GeminiClient] Auto-forcing response with turnComplete');
            await this.forceResponse();
          }
        }, 2000);
      }
    }, GeminiDirectClient.AUTO_NUDGE_DELAY_MS);
  }

  /**
   * Cancel auto-nudge timer (called when response received)
   */
  private cancelAutoNudge(): void {
    if (this.autoNudgeTimer) {
      clearTimeout(this.autoNudgeTimer);
      this.autoNudgeTimer = null;
    }
    this.hasNudged = false;
  }

  /**
   * Send audio data to Gemini
   */
  async sendAudio(audioData: ArrayBuffer): Promise<void> {
    if (!this.session || !this.isConnected || this.isPaused) return;

    try {
      const base64Audio = this.arrayBufferToBase64(audioData);
      await this.session.sendRealtimeInput({
        audio: {
          data: base64Audio,
          mimeType: 'audio/pcm;rate=16000'
        }
      });

      // Track audio sent time and schedule auto-nudge
      this.lastAudioSentTime = Date.now();
      this.scheduleAutoNudge();
    } catch (error) {
      console.error('[GeminiClient] Error sending audio:', error);
    }
  }

  /**
   * Send audio as base64 string
   */
  async sendAudioBase64(base64Audio: string): Promise<void> {
    if (!this.session || !this.isConnected || this.isPaused) return;

    try {
      await this.session.sendRealtimeInput({
        audio: {
          data: base64Audio,
          mimeType: 'audio/pcm;rate=16000'
        }
      });

      // Track audio sent time and schedule auto-nudge
      this.lastAudioSentTime = Date.now();
      this.scheduleAutoNudge();
    } catch (error) {
      console.error('[GeminiClient] Error sending audio:', error);
    }
  }

  /**
   * Send text message to Gemini
   */
  async sendText(text: string): Promise<void> {
    if (!this.session || !this.isConnected || this.isPaused) return;

    try {
      await this.session.sendClientContent({
        turns: {
          role: 'user',
          parts: [{ text }]
        },
        turnComplete: true
      });
    } catch (error) {
      console.error('[GeminiClient] Error sending text:', error);
    }
  }

  /**
   * Nudge Gemini to respond when it's silent
   * Flushes cached audio and prompts a response
   * Use when VAD doesn't detect end of speech correctly
   * Note: audioStreamEnd is for automatic VAD mode (our config)
   *       activityEnd is only for manual VAD mode (disabled: true)
   */
  async nudgeResponse(): Promise<void> {
    if (!this.session || !this.isConnected || this.isPaused) return;

    try {
      console.log('[GeminiClient] Nudging with audioStreamEnd...');
      // Flush cached audio to trigger model response (correct for automatic VAD)
      await this.session.sendRealtimeInput({
        audioStreamEnd: {}
      });
    } catch (error) {
      console.error('[GeminiClient] Error nudging response:', error);
    }
  }

  /**
   * Send an empty turn to force Gemini to respond
   * Fallback when audioStreamEnd doesn't work
   */
  async forceResponse(): Promise<void> {
    if (!this.session || !this.isConnected || this.isPaused) return;

    try {
      console.log('[GeminiClient] Forcing response with turnComplete...');
      await this.session.sendClientContent({
        turnComplete: true
      });
    } catch (error) {
      console.error('[GeminiClient] Error forcing response:', error);
    }
  }

  /**
   * Pause session - disconnects but stores handle for resume
   */
  async pause(): Promise<void> {
    if (!this.isConnected) {
      console.log('[GeminiClient] Not connected, nothing to pause');
      return;
    }

    console.log('[GeminiClient] Pausing session...');
    console.log('[GeminiClient] Current session handle:', this.sessionHandle ? this.sessionHandle.substring(0, 30) + '...' : 'none');

    this.isPaused = true;
    this.shouldReconnect = false;

    // Store session handle for resume
    if (this.sessionHandle) {
      this.storeSessionHandle(this.sessionHandle);
      console.log('[GeminiClient] Session handle stored for resume');
    } else {
      console.warn('[GeminiClient] No session handle available to store!');
    }

    // Clear token cache so we get a fresh token on resume
    this.tokenService.clearCache();
    console.log('[GeminiClient] Token cache cleared for clean resume');

    // Close connection
    await this.disconnect();
  }

  /**
   * Resume a paused session
   */
  async resume(): Promise<void> {
    console.log('[GeminiClient] Resuming session...');

    // Check if we can resume
    const storedHandle = this.getStoredSessionHandle();
    console.log('[GeminiClient] Stored session handle:', storedHandle ? storedHandle.substring(0, 30) + '...' : 'none');

    if (storedHandle) {
      this.sessionHandle = storedHandle;
      console.log('[GeminiClient] Will attempt to resume with stored handle');
    } else {
      console.warn('[GeminiClient] No stored handle - will start new session');
    }

    this.isPaused = false;
    this.shouldReconnect = true;

    // IMPORTANT: Clear token cache to get a fresh token for resume
    // Each ephemeral token can only be used once (uses: 1)
    this.tokenService.clearCache();
    console.log('[GeminiClient] Cleared token cache for fresh token');

    // Reconnect with stored session handle
    await this.connect();
  }

  /**
   * Disconnect from Gemini
   */
  async disconnect(): Promise<void> {
    console.log('[GeminiClient] Disconnecting...');
    this.shouldReconnect = false;
    this.isConnected = false;

    // Clear auto-nudge timer
    this.cancelAutoNudge();

    if (this.session) {
      try {
        this.session.close();
      } catch {
        // Ignore close errors
      }
      this.session = null;
    }

    this.client = null;
  }

  /**
   * Store session handle to localStorage
   */
  private storeSessionHandle(handle: string): void {
    try {
      localStorage.setItem(SESSION_HANDLE_KEY, handle);
      localStorage.setItem(SESSION_HANDLE_TIMESTAMP_KEY, Date.now().toString());
    } catch {
      // localStorage might not be available
    }
  }

  /**
   * Get stored session handle if still valid (within 2hr window)
   */
  private getStoredSessionHandle(): string | null {
    try {
      const handle = localStorage.getItem(SESSION_HANDLE_KEY);
      const timestamp = localStorage.getItem(SESSION_HANDLE_TIMESTAMP_KEY);

      if (!handle || !timestamp) return null;

      // Check if handle is still valid (2hr window)
      const age = Date.now() - parseInt(timestamp, 10);
      if (age > SESSION_RESUME_WINDOW) {
        this.clearStoredSessionHandle();
        return null;
      }

      return handle;
    } catch {
      return null;
    }
  }

  /**
   * Clear stored session handle
   */
  clearStoredSessionHandle(): void {
    try {
      localStorage.removeItem(SESSION_HANDLE_KEY);
      localStorage.removeItem(SESSION_HANDLE_TIMESTAMP_KEY);
    } catch {
      // Ignore
    }
    this.sessionHandle = null;
  }

  /**
   * Convert base64 to ArrayBuffer
   */
  private base64ToArrayBuffer(base64: string): ArrayBuffer {
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
  }

  /**
   * Convert ArrayBuffer to base64
   */
  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.length; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

  // Getters
  get connected(): boolean {
    return this.isConnected;
  }

  get paused(): boolean {
    return this.isPaused;
  }

  get currentSessionHandle(): string | null {
    return this.sessionHandle;
  }

  get canResume(): boolean {
    return this.getStoredSessionHandle() !== null;
  }

  get usage(): UsageMetadata {
    return { ...this.tokenUsage };
  }

  /**
   * Reset token usage counter
   */
  resetUsage(): void {
    this.tokenUsage = {
      inputTokens: 0,
      outputTokens: 0,
      totalTokens: 0
    };
  }
}
